import { useState } from 'react';
import { STEP_UP_NOTICE, dismiss } from '../lib/errorNotices';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button, CodeInput, Field, Input } from '@promanage/ui';
import { QrCode } from '../components/QrCode';
import { AuthSplit } from '../features/auth/AuthSplit';
import { authGate } from '../features/auth/gate';
import { useSession } from '../features/auth/session';
import { queryClient, trpc } from '../lib/trpc';

export const Route = createFileRoute('/_auth/mfa')({
  component: MfaPage,
});

/**
 * The second factor.
 *
 * Two jobs behind one route, because from the user's point of view they are the same moment:
 * either they have an authenticator and need to produce a code, or they do not have one and
 * must set it up before going any further. Which of the two is decided by `authGate` from the
 * session, never by how they arrived here.
 *
 * The root guard sends every unsatisfied session here and bounces satisfied ones away, so
 * this page never has to decide whether it should be visible.
 */
function MfaPage() {
  const session = useSession();

  // The guard resolved the session before this rendered; this is the paint between the cache
  // read and the component, not a real unauthenticated state.
  if (!session) return null;

  return authGate(session) === 'enrol' ? <Enrol /> : <Verify />;
}

// ── Producing a code ─────────────────────────────────────────────────────────

function Verify() {
  const navigate = useNavigate();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');

  const done = () => {
    // The session's capabilities just changed. Everything cached was fetched by a session
    // that could not read most of it.
    queryClient.clear();
    // The "Verify it's you" notice has been answered. Left up, it would sit on the next
    // screen asking for a code that has just been given.
    dismiss(STEP_UP_NOTICE);
    void navigate({ to: '/', replace: true });
  };

  const verify = useMutation(
    trpc.auth.verifyMfa.mutationOptions({
      meta: { errorTitle: 'That code was not accepted', credentials: true },
      onSuccess: done,
    }),
  );
  const recover = useMutation(
    trpc.auth.useRecoveryCode.mutationOptions({
      meta: { errorTitle: 'That recovery code was not accepted', credentials: true },
      onSuccess: done,
    }),
  );

  const active = useRecovery ? recover : verify;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (useRecovery) recover.mutate({ code: code.trim() });
    else verify.mutate({ code: code.trim() });
  };

  return (
    <AuthSplit
      title="Enter your code"
      intro={
        useRecovery
          ? 'Use one of the recovery codes you saved when you set up your authenticator. Each one works once.'
          : 'Open your authenticator app and enter the 6-digit code for ProManage.'
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={useRecovery ? 'Recovery code' : 'Authenticator code'} required>
          {(props) =>
            useRecovery ? (
              <Input
                {...props}
                // A recovery code is neither six digits nor numeric, so it keeps the plain
                // field. Still `one-time-code`: a manager may have it saved beside the login.
                autoComplete="one-time-code"
                autoFocus
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="font-mono"
              />
            ) : (
              <CodeInput {...props} autoFocus value={code} onValueChange={setCode} />
            )
          }
        </Field>

        <Button type="submit" size="lg" disabled={active.isPending}>
          {active.isPending ? 'Checking' : 'Continue'}
        </Button>
      </form>

      <button
        type="button"
        className="mx-auto mt-5 block text-[0.8125rem] font-medium text-accent hover:underline"
        onClick={() => {
          setUseRecovery((v) => !v);
          setCode('');
        }}
      >
        {useRecovery ? 'Use my authenticator instead' : 'I do not have my authenticator'}
      </button>
    </AuthSplit>
  );
}

// ── Setting one up ───────────────────────────────────────────────────────────

function Enrol() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');

  const start = useMutation(
    trpc.auth.startMfaEnrolment.mutationOptions({
      meta: { errorTitle: 'Could not start setting up your authenticator', credentials: true },
    }),
  );
  const confirm = useMutation(
    trpc.auth.confirmMfaEnrolment.mutationOptions({
      meta: { errorTitle: 'That code was not accepted', credentials: true },
    }),
  );

  // Shown exactly once, by the server, and never retrievable again.
  if (confirm.data) {
    return (
      <RecoveryCodes
        codes={confirm.data.recoveryCodes}
        onDone={() => {
          queryClient.clear();
          void navigate({ to: '/', replace: true });
        }}
      />
    );
  }

  if (!start.data) {
    return (
      <AuthSplit
        title="Set up your authenticator"
        intro="Your role can see or move trust money, so ProManage requires a second factor. You will need an authenticator app such as 1Password, Authy or Google Authenticator."
      >
        <Button
          size="lg"
          className="w-full"
          disabled={start.isPending}
          onClick={() => start.mutate()}
        >
          {start.isPending ? 'Preparing' : 'Begin setup'}
        </Button>
      </AuthSplit>
    );
  }

  return (
    <AuthSplit
      title="Add ProManage to your app"
      heading="Pair your authenticator"
      intro="Scan this with your authenticator app — or enter the key by hand — then type the code it shows to confirm."
    >
      <SetupPairing secret={start.data.secret} uri={start.data.uri} />

      <form
        className="mt-5 flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          confirm.mutate({ code: code.trim() });
        }}
      >
        <Field label="Code from your app" required>
          {(props) => <CodeInput {...props} autoFocus value={code} onValueChange={setCode} />}
        </Field>

        <Button type="submit" size="lg" disabled={confirm.isPending}>
          {confirm.isPending ? 'Confirming' : 'Confirm'}
        </Button>
      </form>
    </AuthSplit>
  );
}

/**
 * The three ways to get the secret into an authenticator: scan it, type it, or hand it to an
 * app on this device.
 *
 * All three are on screen at once rather than behind a "can't scan?" disclosure. Scanning
 * fails for ordinary reasons — no camera, a desktop authenticator, a locked-down phone — and
 * a property manager halfway through setting up their login is the worst moment to make
 * someone hunt for the fallback.
 */
function SetupPairing({ secret, uri }: { secret: string; uri: string }) {
  const [copied, setCopied] = useState(false);

  // Grouped in fours. A 32-character unbroken base32 string is transcribed wrongly often
  // enough that the failure looks like a broken authenticator rather than a typo.
  const grouped = secret.replace(/(.{4})/g, '$1 ').trim();

  return (
    <div className="flex flex-col items-center gap-4">
      {/*
       * The white plate is `bg-white`, not a surface token, for the same reason the SVG paints
       * its own white: the code must stay dark-on-light in dark mode to be scannable. The
       * padding sits outside the symbol's quiet zone so the rounded corners cannot eat into it.
       */}
      {/*
       * Sized to the card rather than fixed, because the symbol is not a fixed size either.
       * The URI runs ~137 characters for a short email and longer for a real one, which is a
       * version 8–10 symbol: 57 to 65 modules once the quiet zone is counted. At the ~272px
       * this resolves to in the auth column that is 4–5 device pixels per module, which is what
       * a phone camera needs to read a screen at arm's length. Shrinking this is a scanning
       * bug, not a layout preference.
       */}
      <div className="w-full max-w-[17rem] rounded-md bg-white p-2 ring-1 ring-rule">
        <QrCode
          value={uri}
          // Names the code; does not read out the secret. The key itself is directly below as
          // selectable text, which is the accessible path to the same data.
          label="QR code for pairing your authenticator app with ProManage"
          className="block h-auto w-full"
        />
      </div>

      <div className="w-full rounded-md bg-surface-sunk p-4">
        <p className="text-[0.75rem] font-semibold text-ink-muted">Or enter this setup key</p>
        {/* Wraps between groups, never inside one — a split group is the typo the grouping prevents. */}
        <p className="mt-1.5 font-mono text-[0.9375rem] leading-relaxed break-words select-all">
          {grouped}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(secret).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            {copied ? 'Copied' : 'Copy key'}
          </Button>

          <a
            href={uri}
            className="text-[0.8125rem] font-medium text-accent hover:underline"
            // An otpauth: link is handled by an installed app; there is nothing to navigate to.
            rel="noreferrer"
          >
            Open in authenticator app
          </a>
        </div>
      </div>
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);

  return (
    <AuthSplit
      title="Save your recovery codes"
      intro="These are the only way back in if you lose your authenticator. Each one works once. They are not stored anywhere we can read, so we cannot show them again."
    >
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md bg-surface-sunk p-4 font-mono text-[0.875rem]">
        {codes.map((code) => (
          <li key={code} className="select-all">
            {code}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void navigator.clipboard.writeText(codes.join('\n'))}
        >
          Copy all
        </Button>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-2.5 text-[0.8125rem]">
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
          className="mt-0.5 size-4 accent-[var(--color-accent)]"
        />
        <span>I have saved these somewhere I can get to without this device.</span>
      </label>

      <Button size="lg" className="mt-4 w-full" disabled={!saved} onClick={onDone}>
        Continue to ProManage
      </Button>
    </AuthSplit>
  );
}
